import {createSlice, PayloadAction} from '@reduxjs/toolkit';

import {CommentContentType} from 'sweet-types';

export interface SettingsState {
  reloadRequired: boolean;
  widgetIsOpen: Record<string, boolean>;
  sidebarIsOpen: Record<string, boolean>;
}

const initialState: SettingsState = {
  reloadRequired: false,
  widgetIsOpen: {},
  sidebarIsOpen: {},
};

export const SWEETAI_DASHBOARD_CHAT_WIDGET = 'dashboard-chat-widget';
export const COMMENTS_WIDGET = 'comments';
export const DIAGRAM_WIDGET = 'diagram';
export const SWEETAI_CHAT_WIDGET = 'sweetai-chat';

export type WidgetIsOpenPayloadType = {
  contentType: CommentContentType | 'accountprofile';
  objectId: string | number;
  widgetName:
    | typeof SWEETAI_DASHBOARD_CHAT_WIDGET
    | typeof COMMENTS_WIDGET
    | typeof DIAGRAM_WIDGET
    | typeof SWEETAI_CHAT_WIDGET;
  defaultValue?: boolean;
};

export const WidgetNameLabel: Record<
  WidgetIsOpenPayloadType['widgetName'],
  string
> = {
  [SWEETAI_DASHBOARD_CHAT_WIDGET]: 'Sweet AI',
  [COMMENTS_WIDGET]: 'comments',
  [DIAGRAM_WIDGET]: 'diagram',
  [SWEETAI_CHAT_WIDGET]: 'Sweet AI',
};

export const ALL_AVAILABLE_WIDGETS: Array<
  WidgetIsOpenPayloadType['widgetName']
> = [
  SWEETAI_DASHBOARD_CHAT_WIDGET,
  COMMENTS_WIDGET,
  DIAGRAM_WIDGET,
  SWEETAI_CHAT_WIDGET,
];

export const VERSIONS_SIDEBAR_TOGGABLE = 'versions';

export const ALL_AVAILABLE_DASHBOARDS = [VERSIONS_SIDEBAR_TOGGABLE];

export type DashboardIsOpenPayloadType = {
  contentType: CommentContentType;
  objectId: string | number;
  widgetName: typeof VERSIONS_SIDEBAR_TOGGABLE;
};

const settingsSlice = createSlice({
  name: 'settings',
  initialState,
  reducers: {
    setReloadRequired: (state, action: PayloadAction<boolean>) => {
      state.reloadRequired = action.payload;
    },
    toggleWidgetIsOpen: (
      state,
      action: PayloadAction<WidgetIsOpenPayloadType>
    ) => {
      const {
        contentType,
        objectId,
        widgetName,
        defaultValue = true,
      } = action.payload;
      const key = `${contentType} ${objectId} ${widgetName}`;
      state.widgetIsOpen[key] = !(state.widgetIsOpen[key] ?? defaultValue);
    },
    toggleSidebarIsOpen: (
      state,
      action: PayloadAction<DashboardIsOpenPayloadType>
    ) => {
      const {contentType, objectId, widgetName} = action.payload;

      const key = `${contentType} ${objectId} ${widgetName}`;
      state.sidebarIsOpen[key] = !(state.sidebarIsOpen[key] ?? true);
    },
  },
});

export const {setReloadRequired, toggleWidgetIsOpen, toggleSidebarIsOpen} =
  settingsSlice.actions;

export const settingsReducer = settingsSlice.reducer;
